import React, { useState } from 'react';
import { Button, Checkbox, Collapse, ColorPicker, Form, InputNumber, Segmented, Space, Switch,
  Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { GD_TOOLS, TGraphicDesignLessonConfig, TGDTool } from '../../../models/courses/graphicDesign';
import { findRule, setTimeLimit } from './ruleTemplates';
import { isIntentRead } from './steps';

interface Props { config: TGraphicDesignLessonConfig;
  onChange: (patch: Partial<TGraphicDesignLessonConfig>) => void; readOnly?: boolean }
const formats = [{ value: 'portrait', width: 600, height: 800 },
  { value: 'square', width: 600, height: 600 },
  { value: 'landscape', width: 800, height: 600 }];
const AdvancedSettings: React.FC<Props> = ({ config, onChange, readOnly }) => {
  const { t } = useTranslation();
  const [custom, setCustom] = useState(false);
  const preset = formats.find(item => item.width === config.artboard.width &&
    item.height === config.artboard.height)?.value;
  const format = custom ? 'custom' : (preset ?? 'custom');
  const time = findRule(config.rules, 'timeMax')?.params.seconds;
  // Outils, palette et limite d'elements ne servent que si l'eleve compose lui-meme.
  const composes = ['CREATE', 'TRANSFORM'].includes(config.exerciseType) ||
    (config.exerciseType === 'INTENT' && !isIntentRead(config));
  const palette = config.lockedPalette;
  const setColor = (index: number, color: string) => onChange({ lockedPalette:
    (palette ?? []).map((old, i) => i === index ? color : old) });
  return <Collapse items={[{ key: 'advanced', label: t('graphicDesignLesson.admin.advanced'),
    extra: <Typography.Text type="secondary">{t('graphicDesignLesson.admin.advancedHelp')}</Typography.Text>,
    children: <Form layout="vertical">
      <Form.Item label={t('graphicDesignLesson.admin.format')}>
        <Segmented value={format} disabled={readOnly} options={
          ['portrait', 'square', 'landscape', 'custom'].map(value => ({ value,
            label: t(`graphicDesignLesson.admin.formats.${value}`) }))}
          onChange={value => { const preset = formats.find(item => item.value === value);
            if (preset) {
              setCustom(false);
              onChange({ artboard: { width: preset.width, height: preset.height } });
            } else setCustom(true); }} />
      </Form.Item>
      {format === 'custom' && <Space wrap>
        <Form.Item label={t('graphicDesignLesson.admin.width')}><InputNumber min={100} max={2000}
          addonAfter="px" disabled={readOnly} value={config.artboard.width}
          onChange={width => width && onChange({ artboard: { ...config.artboard, width } })} /></Form.Item>
        <Form.Item label={t('graphicDesignLesson.admin.height')}><InputNumber min={100} max={2000}
          addonAfter="px" disabled={readOnly} value={config.artboard.height}
          onChange={height => height && onChange({ artboard: { ...config.artboard, height } })} /></Form.Item>
      </Space>}
      <Form.Item label={t('graphicDesignLesson.admin.background')}><ColorPicker showText
        disabled={readOnly} value={config.background}
        onChangeComplete={color => onChange({ background: color.toHexString() })} /></Form.Item>
      {composes && <Form.Item label={t('graphicDesignLesson.admin.lockedPalette')}
        extra={t('graphicDesignLesson.admin.lockedPaletteHelp')}>
        <Space wrap><Switch checked={!!palette} disabled={readOnly}
          aria-label={t('graphicDesignLesson.admin.lockedPalette')}
          onChange={checked => onChange({ lockedPalette: checked
            ? Array.from(new Set([config.background, '#1f2a37', '#c2410c'])) : undefined })} />
          {palette?.map((color, index) => <Space.Compact key={index}>
            <ColorPicker value={color} disabled={readOnly}
              onChangeComplete={next => setColor(index, next.toHexString())} />
            <Button icon={<DeleteOutlined />} disabled={readOnly || palette.length <= 1}
              aria-label={t('graphicDesignLesson.admin.removeColor', { n: index + 1 })}
              onClick={() => onChange({ lockedPalette: palette.filter((_, i) => i !== index) })} />
          </Space.Compact>)}
          {palette && palette.length < 8 && <Button type="dashed" icon={<PlusOutlined />}
            disabled={readOnly} onClick={() => onChange({ lockedPalette: [...palette, '#2d9cc9'] })}>
            {t('graphicDesignLesson.admin.addColor')}</Button>}</Space>
      </Form.Item>}
      {composes && <><Form.Item label={t('graphicDesignLesson.admin.allowedTools')}
        extra={t('graphicDesignLesson.admin.allowedToolsHelp')}>
        <Checkbox.Group value={config.allowedTools} disabled={readOnly}
          options={GD_TOOLS.map(tool => ({ value: tool,
            label: t(`graphicDesignLesson.editor.tool.${tool}`) }))}
          onChange={values => onChange({ allowedTools: values as TGDTool[] })} />
      </Form.Item>
      <Form.Item label={t('graphicDesignLesson.admin.maxElements')}>
        <InputNumber min={1} disabled={readOnly} value={config.maxElements}
          placeholder={t('graphicDesignLesson.admin.noLimit')}
          onChange={maxElements => onChange({ maxElements: maxElements ?? undefined })} />
      </Form.Item></>}
      <Form.Item label={t('graphicDesignLesson.admin.timeBonus')}>
        <Space wrap><Switch checked={time !== undefined} disabled={readOnly}
          onChange={checked => onChange({ rules: setTimeLimit(config.rules,
            checked ? 480 : undefined) })} />
          {time !== undefined && <>{t('graphicDesignLesson.admin.deliverWithin')}{' '}
            <InputNumber min={1} disabled={readOnly} value={Math.round(time / 60)}
              onChange={minutes => onChange({ rules: setTimeLimit(config.rules,
                (minutes ?? 1) * 60) })} /> {t('graphicDesignLesson.admin.minutes')}</>}</Space>
      </Form.Item>
    </Form>,
  }]} />;
};
export default AdvancedSettings;
